import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import { DialogFooter } from "../../../../../components/ui/dialog";
import {
  type AddressSnapshotFields,
  type BlockingVerificationStatus,
  isAddressValidForVerification,
} from "../addressVerification";

export const ADDRESS_VERIFICATION_MESSAGE_KEYS = {
  WarningChanged: "review.customer.residence.addressVerification.messages.warningChanged",
  ErrorSubpremise: "review.customer.residence.addressVerification.messages.errorSubpremise",
  ErrorFailed: "review.customer.residence.addressVerification.messages.errorFailed",
} as const satisfies Record<BlockingVerificationStatus, string>;

const IGNORE_CONFIRM_KEYS = {
  ErrorSubpremise: "review.customer.residence.addressVerification.ignoreConfirm.errorSubpremise",
  ErrorFailed: "review.customer.residence.addressVerification.ignoreConfirm.errorFailed",
} as const satisfies Record<Exclude<BlockingVerificationStatus, "WarningChanged">, string>;

// ---------------------------------------------------------------------------
// AddressVerificationPanel (address-standardization-spec.md §8)
//
// The status message, "You Entered" / "USPS Suggested" panels, ignore
// confirmation and footer actions, without dialog chrome. Shared by the
// Residence tab's AddressVerificationModal and the residence Call Script's
// in-modal address-compare step. Local ignore-confirm state resets when the
// caller remounts it (pass a `key` per verification).
//
// Both panels are read-only text — to fix an address, the analyst uses
// "Edit address", which returns them to wherever the address is edited.
// There is no footer Cancel (OR-25115): the host's header close aborts the
// flow, and "Go back" on the ignore confirmation does the same rather than
// returning to the first step.
// ---------------------------------------------------------------------------

interface AddressVerificationPanelProperties {
  status: BlockingVerificationStatus;
  entered: AddressSnapshotFields;
  suggested?: AddressSnapshotFields;
  onSelect: (fields: AddressSnapshotFields) => void;
  onIgnore: () => void;
  /** Called with the "You Entered" address, so the analyst can fix it. */
  onEdit: (fields: AddressSnapshotFields) => void;
  /** Called from the ignore confirmation's "Go back"; aborts the flow, same as the host's header close. */
  onCancel: () => void;
}

interface AddressDisplayPanelProperties {
  title: string;
  fields: AddressSnapshotFields;
  invalid?: boolean;
}

/** Read-only rendering of an address as plain text. */
function AddressDisplayPanel({ title, fields, invalid }: AddressDisplayPanelProperties) {
  const { t } = useTranslation();
  const apt = fields.apartmentNumber.trim();
  const street = [fields.streetNumber, fields.streetName].filter((part) => part.trim() !== "").join(" ");
  const streetLine = [street, apt && `${t("review.customer.residence.addressVerification.aptDisplayPrefix")} ${apt}`]
    .filter(Boolean)
    .join(", ");
  const cityLine = [fields.city.trim(), [fields.state.trim(), fields.zip.trim()].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  const lines = [streetLine, cityLine].filter((line) => line !== "");

  return (
    <div className="flex flex-1 flex-col items-center gap-2">
      <h4 className="text-primary text-xs font-semibold">{title}</h4>
      <div className="text-center text-sm">
        {lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
      {invalid && (
        <p role="alert" className="text-destructive text-2xs">
          {t("review.customer.residence.addressVerification.invalidPanel")}
        </p>
      )}
    </div>
  );
}

export function AddressVerificationPanel({
  status,
  entered,
  suggested,
  onSelect,
  onIgnore,
  onEdit,
  onCancel,
}: AddressVerificationPanelProperties) {
  const { t } = useTranslation();
  const [confirmingIgnore, setConfirmingIgnore] = useState(false);

  // USPS's own suggestion isn't guaranteed to satisfy the same pre-flight
  // check `entered` already passed — a malformed response (e.g. bad state/zip)
  // shouldn't be selectable, so "Select Suggested" is disabled for it and the
  // analyst falls back to "Select Entered" or the header close.
  const suggestedInvalid = suggested !== undefined && !isAddressValidForVerification(suggested);

  function handleSelectSuggested() {
    if (!suggested || suggestedInvalid) {
      return;
    }
    onSelect(suggested);
  }

  return (
    <>
      <div className="flex flex-col gap-4 bg-white px-5 py-3">
        <p className="text-sm">{t(ADDRESS_VERIFICATION_MESSAGE_KEYS[status])}</p>

        <div className="flex flex-col gap-4 sm:flex-row">
          <AddressDisplayPanel title={t("review.customer.residence.addressVerification.youEntered")} fields={entered} />
          {status === "WarningChanged" && suggested && (
            <AddressDisplayPanel
              title={t("review.customer.residence.addressVerification.uspsSuggested")}
              fields={suggested}
              invalid={suggestedInvalid}
            />
          )}
        </div>

        {confirmingIgnore && status !== "WarningChanged" && (
          <p role="alert" className="text-destructive text-sm">
            {t(IGNORE_CONFIRM_KEYS[status])}
          </p>
        )}
      </div>

      <DialogFooter className="bg-muted border-t px-5 py-2 sm:justify-center">
        {status === "WarningChanged" ? (
          <>
            {/* `entered` already passed isAddressValidForVerification before
              verification ran, so it needs no re-check here. */}
            <Button type="button" size="xs" onClick={() => onSelect(entered)}>
              {t("review.customer.residence.addressVerification.selectEntered")}
            </Button>
            <Button type="button" size="xs" onClick={handleSelectSuggested} disabled={suggestedInvalid}>
              {t("review.customer.residence.addressVerification.selectSuggested")}
            </Button>
          </>
        ) : confirmingIgnore ? (
          <>
            <Button type="button" variant="outline" size="xs" onClick={onCancel}>
              {t("review.customer.residence.addressVerification.goBack")}
            </Button>
            <Button type="button" size="xs" onClick={onIgnore}>
              {t("review.customer.residence.addressVerification.confirmIgnore")}
            </Button>
          </>
        ) : (
          <>
            <Button type="button" variant="outline" size="xs" onClick={() => setConfirmingIgnore(true)}>
              {t("review.customer.residence.addressVerification.ignore")}
            </Button>
            <Button type="button" size="xs" onClick={() => onEdit(entered)}>
              {t("review.customer.residence.addressVerification.editAddress")}
            </Button>
          </>
        )}
      </DialogFooter>
    </>
  );
}
